import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";
import {
  AMBER_INK,
  AMBER_SOFT_BG,
  GREEN_INK,
  GREEN_SOFT_BG,
  VIOLET_SOFT_BG,
} from "apps/project-iq/utils/diagnosticUtils";

const SKELETON_BREADCRUMB_WIDTH = "120px";
const SKELETON_HERO_NAME_WIDTH = "260px";
const SKELETON_HERO_SUBLINE_WIDTH = "180px";
const SKELETON_SMALL_WIDTH = "60px";
const SKELETON_TAB_PRIMARY_WIDTH = "80px";
const SKELETON_TAB_SECONDARY_WIDTH = "72px";
const SKELETON_CONTENT_HEIGHT = "200px";

export const useDiagnosticDetailPageStyles = makeStyles({
  root: {
    height: "100%",
    overflowY: "auto",
  },

  breadcrumbBar: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    height: "64px",
    borderBottom: `1px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    display: "flex",
    alignItems: "center",
    padding: "0 28px",
    gap: "12px",
  },

  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    border: `1px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    color: projectIqTokens.muted,
    height: "36px",
    padding: "0 13px",
    borderRadius: "9px",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: projectIqTokens.fontFamily,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.violet}`,
      outlineOffset: "2px",
    },
  },

  breadcrumbSeparator: {
    color: projectIqTokens.faint,
  },

  breadcrumbDiagnosticName: {
    fontSize: "14.5px",
    fontWeight: 700,
    color: projectIqTokens.ink,
  },

  contentArea: {
    maxWidth: "920px",
    margin: "0 auto",
    padding: "32px 28px 0px 28px",
  },

  hero: {
    "@keyframes diagIn": {
      from: { opacity: 0, transform: "translateY(8px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    animationName: "diagIn",
    animationDuration: "0.4s",
    animationTimingFunction: "ease",
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationDuration: "0.01ms",
    },
    marginBottom: "22px",
    display: "flex",
    alignItems: "center",
    gap: "13px",
  },

  methodIconTile: {
    width: "44px",
    height: "44px",
    borderRadius: designTokens.borderRadius.lg,
    flexShrink: 0,
    background: VIOLET_SOFT_BG,
    color: projectIqTokens.violet,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
  },

  heroBody: {
    flex: 1,
    minWidth: 0,
  },

  heroNameRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },

  heroName: {
    fontSize: "26px",
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.02em",
    color: projectIqTokens.ink,
  },

  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.05em",
    padding: "4px 10px",
    borderRadius: "6px",
    textTransform: "uppercase",
  },

  statusPillInProgress: {
    backgroundColor: AMBER_SOFT_BG,
    color: AMBER_INK,
  },

  statusPillCompleted: {
    backgroundColor: GREEN_SOFT_BG,
    color: GREEN_INK,
  },

  statusPillDraft: {
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.muted,
  },

  heroSubLine: {
    fontSize: "13px",
    color: projectIqTokens.muted,
    marginTop: "4px",
  },

  heroMethodLabel: {
    color: projectIqTokens.violet,
    fontWeight: 700,
  },

  heroCreatedDate: {
    color: projectIqTokens.faint,
  },

  tabBar: {
    display: "flex",
    gap: "4px",
    borderBottom: `1px solid ${projectIqTokens.line}`,
    marginBottom: "24px",
  },

  tab: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: "13.5px",
    fontWeight: 700,
    color: projectIqTokens.muted,
    marginBottom: "-1px",
    transition: designTokens.transition.fast,
    fontFamily: projectIqTokens.fontFamily,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.violet}`,
      outlineOffset: "2px",
    },
  },

  tabActive: {
    borderBottom: `2px solid ${projectIqTokens.teal}`,
    color: projectIqTokens.ink,
  },

  skeletonRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  skeletonCol: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: 1,
    minWidth: 0,
  },

  runCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: "24px",
    fontFamily: projectIqTokens.fontFamily,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },

  diagnosticChatCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 260px)",
    minHeight: "380px",
    overflow: "hidden",
  },

  runHeading: {
    fontSize: designTokens.fontSize.xl,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: 0,
    marginBottom: "6px",
  },

  runSubtitle: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    marginTop: "4px",
    marginBottom: "20px",
  },

  runRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "16px",
    padding: "14px 16px",
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: "10px",
  },

  runRowBody: {
    flex: 1,
    minWidth: 0,
  },

  runRowTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: 0,
  },

  runRowDesc: {
    fontSize: "11px",
    color: projectIqTokens.muted,
    marginTop: "2px",
    lineHeight: "1.5",
  },

  runOpenButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "36px",
    padding: "0 16px",
    borderRadius: "9px",
    backgroundColor: projectIqTokens.violet,
    border: "none",
    color: projectIqTokens.sidebarTextStrong,
    fontSize: "13px",
    fontWeight: 700,
    textDecoration: "none",
    flexShrink: 0,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.ink}`,
      outlineOffset: "2px",
    },
  },

  runSeparator: {
    height: "1px",
    backgroundColor: projectIqTokens.line,
    margin: "20px 0",
  },

  runFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },

  runFooterText: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
  },

  runMarkButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "36px",
    padding: "0 16px",
    borderRadius: "9px",
    backgroundColor: projectIqTokens.tealInk,
    color: projectIqTokens.sidebarTextStrong,
    fontSize: "13px",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
    flexShrink: 0,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.ink}`,
      outlineOffset: "2px",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: "0.65",
    },
  },

  overviewPanel: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[6],
  },

  infoCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: designTokens.spacing[5],
    fontFamily: projectIqTokens.fontFamily,
  },

  infoCardHeading: {
    fontSize: designTokens.fontSize.xl,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: 0,
    marginBottom: "6px",
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    gap: "8px 16px",
    margin: 0,
  },

  infoLabel: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[500],
  },

  infoValue: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[900],
    margin: 0,
  },

  templateCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: designTokens.spacing[5],
    fontFamily: projectIqTokens.fontFamily,
  },

  templateCardHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
    marginBottom: designTokens.spacing[2],
  },

  templateCardDot: {
    width: "9px",
    height: "9px",
    borderRadius: "50%",
    flexShrink: 0,
  },

  templateCardMeta: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    textTransform: "uppercase" as const,
    color: designTokens.neutral[500],
    letterSpacing: "0.05em",
  },

  templateCardDesc: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[700],
    lineHeight: "1.6",
    marginBottom: designTokens.spacing[3],
  },

  templateCardWhenToUse: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.accent[500],
    fontStyle: "italic",
    marginBottom: designTokens.spacing[3],
  },

  templateCardActions: {
    display: "flex",
    flexDirection: "row",
    gap: designTokens.spacing[2],
    flexWrap: "wrap",
  },

  templateCardLinkPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    padding: designTokens.spacing[1] + " " + designTokens.spacing[3],
    border: "1px solid " + designTokens.accent[500],
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.accent[500],
    textDecoration: "none",
    background: "transparent",
    cursor: "pointer",
  },

  templateCardLinkSecondary: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    padding: designTokens.spacing[1] + " " + designTokens.spacing[3],
    border: "1px solid " + designTokens.neutral[400],
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[400],
    textDecoration: "none",
    background: "transparent",
    cursor: "pointer",
  },

  overviewContent: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },

  overviewSection: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
    paddingBottom: designTokens.spacing[4],
    marginBottom: designTokens.spacing[4],
    borderBottom: `1px solid ${projectIqTokens.line}`,
    ":last-child": {
      paddingBottom: "0",
      marginBottom: "0",
      borderBottom: "none",
    },
  },

  overviewSectionTitle: {
    fontSize: "11px",
    fontWeight: 700,
    color: projectIqTokens.faint,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "0 0 4px 0",
  },

  overviewSectionBody: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    margin: "0",
    lineHeight: "1.5",
  },

  tabContentFullWidth: {
    marginLeft: "-28px",
    marginRight: "-28px",
    width: "calc(100% + 56px)",
    overflowX: "auto",
  },

  runSubTabBar: {
    display: "flex",
    gap: "0",
    borderBottom: `1px solid ${projectIqTokens.line}`,
    marginBottom: "22px",
  },

  runSubTab: {
    background: "transparent",
    border: "none",
    borderBottom: "2.5px solid transparent",
    padding: "10px 6px",
    marginRight: "28px",
    marginBottom: "-1px",
    cursor: "pointer",
    fontSize: "14.5px",
    fontWeight: 700,
    color: projectIqTokens.muted,
    transition: designTokens.transition.fast,
    fontFamily: projectIqTokens.fontFamily,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },

  runSubTabActive: {
    borderBottom: `2.5px solid ${projectIqTokens.teal}`,
    color: projectIqTokens.ink,
  },

  skeletonBreadcrumb: {
    width: SKELETON_BREADCRUMB_WIDTH,
  },

  skeletonHeroName: {
    width: SKELETON_HERO_NAME_WIDTH,
  },

  skeletonStatusPill: {
    width: SKELETON_SMALL_WIDTH,
  },

  skeletonHeroSubline: {
    width: SKELETON_HERO_SUBLINE_WIDTH,
  },

  skeletonTabPrimary: {
    width: SKELETON_TAB_PRIMARY_WIDTH,
  },

  skeletonTabSecondary: {
    width: SKELETON_TAB_SECONDARY_WIDTH,
  },

  skeletonTabTertiary: {
    width: SKELETON_SMALL_WIDTH,
  },

  skeletonContent: {
    height: SKELETON_CONTENT_HEIGHT,
    width: "100%",
  },
});
